import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import Imgs from "@/views/components/Upload/Imgs";
import Video from "@/views/components/Upload/Video";
import WangEditor from "@/views/components/WangEditor";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { enterpriseManageService } from "@/api/services/enterpriseManageService";
import { Form, Input, Spin, Typography } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";

import {
    Button as ZhButton,
    Checkbox as ZhCheckbox,
    Dialog as ZhDialog,
    Image as ZhImage,
    InputNumber as ZhInputNumber,
    RadioGroup as ZhRadioGroup,
    Select as ZhSelect,
    Table as ZhTable,
    type TableColumnProps,
    type UploadUserFile
} from "zhihao-ui-react";

import request from "@/api";
import type { ActivityRelatedProductItem } from "./types";
import "./ActivityRelatedProductSelector.scss";

const productTypeLabels: Record<number, string> = {
    1: "寄售商品",
    2: "团购商品",
    3: "捡漏商品",
    4: "活动商品"
};

export interface ActivityRelatedProductCandidate extends ActivityRelatedProductItem {
    mainImgUrl?: string;
    productCode?: string;
    addTime?: string;
    product?: {
        title?: string;
        productTypeName?: string;
        supplierName?: string;
        supplierEnterprise?: { name?: string; enterpriseName?: string; supplierName?: string };
        [key: string]: unknown;
    };
}

export interface ActivityRelatedProductSelectorResult {
    list?: ActivityRelatedProductCandidate[];
    total?: number;
}

export interface ActivityRelatedProductSelectorProps {
    /** 编辑页与 Vue 一致额外展示上架状态和添加时间。 */
    isEdit?: boolean;
    value?: ActivityRelatedProductItem[] | string;
    onChange?: (value: ActivityRelatedProductItem[] | string) => void;
    loadCandidates?: (query: {
        page: number;
        size: number;
        keyword?: string;
        productTypeName?: string;
        supplierName?: string;
        type?: number;
        excludeProducts?: Array<{ productId: string; type: number }>;
    }) => Promise<ActivityRelatedProductSelectorResult>;
}

interface ActivityProductFormValues {
    supplierId?: number | string;
    name?: string;
    stock?: number;
    pricingMethod?: number;
    marketPrice?: number;
    estimatedUnitPrice?: number;
    lowerThanMarketPrice?: number;
    hasOverbooking?: number;
    minOrderTon?: number;
    minAdjustmentTon?: number;
    orderRule?: number;
    intentionDeposit?: number;
    tonStep?: number;
    amountStep?: number;
    detail?: string;
    pictureFileIds?: Array<number | string>;
    videoFileId?: number | string;
}

interface SupplierOption {
    label: string;
    value: number | string;
    supplierEnterprise?: Record<string, unknown>;
}

/** 按旧版 Vue formData 结构构造新增活动商品，确保隐藏图片 ID 一并进入保存载荷。 */
export function buildActivityProductSubmission(
    values: ActivityProductFormValues,
    pictureFileIds: Array<number | string>,
    supplierEnterprise?: Record<string, unknown>,
    mainImgUrl?: string
): ActivityRelatedProductItem {
    return {
        clientId: `new-${Date.now()}`,
        type: 4,
        mainImgUrl,
        product: {
            ...values,
            pictureFileIds,
            title: values.name,
            supplierEnterprise
        }
    };
}

/** 复用 Vue 商品选择器接口，保留活动表单的商品类型和排除项契约。 */
export async function loadActivityRelatedProductCandidates(query: {
    page: number;
    size: number;
    keyword?: string;
    productTypeName?: string;
    supplierName?: string;
    type?: number;
    excludeProducts?: Array<{ productId: string; type: number }>;
}): Promise<ActivityRelatedProductSelectorResult> {
    const response = await request.service.post<ActivityRelatedProductSelectorResult>(
        "/admin/product/campaign-paging-selector",
        query
    );
    return response.data;
}

/** 读取关联商品在列表、编辑回显和新增活动商品中的稳定引用。 */
const itemReference = (item: ActivityRelatedProductItem) =>
    item.id ??
    item.productId ??
    item.productCampaign?.code ??
    item.productCampaign?.id ??
    item.product?.code ??
    item.product?.id ??
    item.clientId;

const itemKey = (item: ActivityRelatedProductItem) => {
    const reference = itemReference(item);
    return item.type == null || reference == null ? "" : `${Number(item.type)}:${String(reference)}`;
};

/** 保留后端商品编号的原始字符串，避免超出 Number.MAX_SAFE_INTEGER 后发生精度丢失。 */
const normalizeItems = (value?: ActivityRelatedProductItem[] | string): ActivityRelatedProductItem[] => {
    const items = Array.isArray(value)
        ? value
        : String(value || "")
              .split(",")
              .map(item => item.trim().split(":"))
              .filter(([type, id]) => type && id)
              .map(([type, id]) => ({ type: Number(type), id }));
    const seen = new Set<string>();
    return items.filter(item => {
        const key = itemKey(item);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

/** 兼容 Vue 商品候选接口的嵌套企业名称和扁平 supplierName 返回结构。 */
const getCandidateSupplierName = (candidate: ActivityRelatedProductCandidate) =>
    candidate.product?.supplierEnterprise?.name ||
    candidate.product?.supplierEnterprise?.enterpriseName ||
    candidate.product?.supplierEnterprise?.supplierName ||
    candidate.product?.supplierName ||
    "-";

const getItemTitle = (item: ActivityRelatedProductItem) =>
    String(item.type) === "4"
        ? String(item.productCampaign?.name || item.product?.name || item.product?.title || "-")
        : String(item.product?.title || "-");

type ActivityRelatedProductFilterState = {
    productTypeName: string;
    supplierName: string;
    candidateType: string;
};

/** 活动商品详情编辑器，维持 Ant Form 的受控字段约定。 */
function ActivityProductEditor({ value, onChange }: { value?: string; onChange?: (value: string) => void }) {
    return <WangEditor height="120px" placeholder="请输入商品详情" value={value} onUpdateValue={onChange} />;
}

/** 严格复刻 Vue ProductList 与 AddProductDialog 的商品列表、选择弹窗和新增活动商品分支。 */
export function ActivityRelatedProductSelector({
    isEdit = false,
    value,
    onChange,
    loadCandidates = loadActivityRelatedProductCandidates
}: ActivityRelatedProductSelectorProps) {
    const [activityProductForm] = Form.useForm<ActivityProductFormValues>();
    const [open, setOpen] = useState(false);
    const [dialogType, setDialogType] = useState(1);
    const [keyword, setKeyword] = useState("");
    const [loading, setLoading] = useState(false);
    const [selectorError, setSelectorError] = useState<string | null>(null);
    const [candidates, setCandidates] = useState<ActivityRelatedProductCandidate[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [productTypeName, setProductTypeName] = useState("");
    const [supplierName, setSupplierName] = useState("");
    const [candidateType, setCandidateType] = useState("");
    const [pendingKeys, setPendingKeys] = useState<string[]>([]);
    const [supplierOptions, setSupplierOptions] = useState<SupplierOption[]>([]);
    const [pictureFiles, setPictureFiles] = useState<UploadUserFile[]>([]);
    const pictureReplacementIndex = useRef<number | null>(null);
    const [mainImgUrl, setMainImgUrl] = useState("");
    const selectedItems = useMemo(() => normalizeItems(value), [value]);
    const pricingMethod = Form.useWatch("pricingMethod", activityProductForm);
    const orderRule = Form.useWatch("orderRule", activityProductForm);

    useEffect(() => {
        if (!open) return;
        // Vue 切换商品分支时从弹窗主体顶部展示；避免焦点恢复把 React 滚动条推下去。
        const frame = requestAnimationFrame(() => {
            const body = document.querySelector<HTMLElement>(".activity-product-dialog-modal .ant-modal-body");
            if (body) body.scrollTop = 0;
        });
        return () => cancelAnimationFrame(frame);
    }, [dialogType, open]);

    const queryCandidates = async (
        nextKeyword = keyword,
        nextPage = 1,
        filterOverrides?: Partial<ActivityRelatedProductFilterState>
    ) => {
        const filters: ActivityRelatedProductFilterState = {
            productTypeName,
            supplierName,
            candidateType,
            ...filterOverrides
        };
        setLoading(true);
        setSelectorError(null);
        setPage(nextPage);
        try {
            // Vue 只把寄售、团购、捡漏商品作为已关联排除项，活动商品由页面本地维护。
            const excludeProducts = selectedItems
                .filter(item => [1, 2, 3].includes(Number(item.type)) && itemReference(item) != null)
                .map(item => ({ productId: String(itemReference(item)), type: Number(item.type) }));
            const result = await loadCandidates({
                page: nextPage,
                size: 10,
                ...(nextKeyword.trim() ? { keyword: nextKeyword.trim() } : {}),
                ...(filters.productTypeName.trim() ? { productTypeName: filters.productTypeName.trim() } : {}),
                ...(filters.supplierName.trim() ? { supplierName: filters.supplierName.trim() } : {}),
                ...(filters.candidateType ? { type: Number(filters.candidateType) } : {}),
                ...(excludeProducts.length ? { excludeProducts } : {})
            });
            setCandidates(result.list || []);
            setTotal(result.total || 0);
        } catch {
            setSelectorError("活动商品候选加载失败");
        } finally {
            setLoading(false);
        }
    };

    /** 打开新增商品弹窗时恢复 Vue 的默认“关联已有商品”状态。 */
    const openDialog = () => {
        setOpen(true);
        setDialogType(1);
        setPendingKeys([]);
        setSelectorError(null);
        activityProductForm.resetFields();
        setPictureFiles([]);
        pictureReplacementIndex.current = null;
        setMainImgUrl("");
        void queryCandidates("", 1);
    };

    const removeItem = (index: number) => onChange?.(selectedItems.filter((_item, itemIndex) => itemIndex !== index));

    /** 旧版活动商品供应商下拉远程搜索，同时保留构造 supplierEnterprise 所需的原始资料。 */
    const querySuppliers = async (searchKeyword = "") => {
        try {
            const suppliers = await enterpriseManageService.selector({ supplierName: searchKeyword || undefined });
            setSupplierOptions(
                suppliers.map(supplier => ({
                    label: supplier.supplierName || "-",
                    value: supplier.id,
                    supplierEnterprise: {
                        code: supplier.code,
                        name: supplier.supplierName,
                        unifiedSocialCreditCode: supplier.socialCreditCode,
                        legalRepresentative: supplier.legalRepresentative,
                        realName: supplier.contactPerson,
                        mobile: supplier.contactPhone
                    }
                }))
            );
        } catch {
            // 供应商候选失败不应中断弹窗交互，用户仍可重试远程搜索。
            setSupplierOptions([]);
        }
    };

    /** 弹窗提交按旧版分支合并已有商品，或构造新增活动商品的 product 结构。 */
    const submitDialog = async () => {
        if (dialogType === 1) {
            const additions = candidates.filter(candidate => pendingKeys.includes(itemKey(candidate)));
            onChange?.(normalizeItems([...selectedItems, ...additions]));
            setOpen(false);
            return;
        }
        const values = await activityProductForm.validateFields();
        const supplier = supplierOptions.find(option => String(option.value) === String(values.supplierId));
        // 图片 ID 存在未注册的表单字段中，validateFields 不保证返回它；按 Vue formData 契约显式传入。
        const activityProduct = buildActivityProductSubmission(
            values,
            activityProductForm.getFieldValue("pictureFileIds") || [],
            supplier?.supplierEnterprise,
            mainImgUrl
        );
        // Vue ProductInfoList 将刚新增的活动商品插入首行，编辑态也保持相同回显顺序。
        onChange?.([activityProduct, ...selectedItems]);
        setOpen(false);
    };

    /** 将上传预览文件的服务端编号按当前图片顺序同步回活动商品表单。 */
    const getPictureFileIds = (files: UploadUserFile[]) =>
        files
            .map(file => (file.response as { id?: number | string } | undefined)?.id)
            .filter((id): id is number | string => id !== undefined && id !== null);

    /** Vue UploadList 以首张图片作为主图，设主图后同时更新展示顺序和提交编号顺序。 */
    const setPictureAsMain = (file: UploadUserFile, index: number) => {
        if (index === 0) return;
        const nextFiles = [...pictureFiles];
        nextFiles.splice(index, 1);
        nextFiles.unshift(file);
        setPictureFiles(nextFiles);
        activityProductForm.setFieldValue("pictureFileIds", getPictureFileIds(nextFiles));
        setMainImgUrl(file.url || "");
    };

    /** 使用同一图片上传入口替换当前卡片，保留文件列表和主图编号的一致性。 */
    const replacePicture = (event: React.MouseEvent<HTMLButtonElement>, index: number) => {
        event.stopPropagation();
        pictureReplacementIndex.current = index;
        const uploadInput = event.currentTarget
            .closest(".activity-product-dialog__upload-list")
            ?.querySelector<HTMLInputElement>('input[type="file"]');
        uploadInput?.click();
    };

    const selectedColumns: TableColumnProps<ActivityRelatedProductItem>[] = [
        { key: "index", label: "序号", width: 68, prop: "index", render: ({ rowIndex }) => rowIndex + 1 },
        {
            key: "productId",
            label: "商品编号",
            width: 176,
            prop: "productId",
            render: ({ row }) => String(itemReference(row) || "-")
        },
        {
            key: "title",
            label: "主图/标题",
            // 主图列在 Vue 中按最小宽度自适应，避免编辑态固定宽度挤掉其余商品列。
            minWidth: 176,
            prop: "title",
            render: ({ row }) => (
                <div className="activity-product-list__title-cell">
                    {row.mainImgUrl ? (
                        <ZhImage preview src={row.mainImgUrl} />
                    ) : (
                        <span className="empty-img">暂无图片</span>
                    )}
                    <span className="ellipsis">{getItemTitle(row)}</span>
                </div>
            )
        },
        {
            key: "productTypeName",
            label: "品类",
            width: 120,
            prop: "productTypeName",
            render: ({ row }) => String(row.product?.productTypeName || "-")
        },
        {
            key: "supplierName",
            label: "供应商",
            minWidth: 148,
            prop: "supplierName",
            render: ({ row }) => getCandidateSupplierName(row as ActivityRelatedProductCandidate)
        },
        {
            key: "type",
            label: "商品类型",
            width: 100,
            prop: "type",
            render: ({ row }) => productTypeLabels[Number(row.type)] || "-"
        },
        ...(isEdit
            ? [
                  {
                      key: "state",
                      label: "上架状态",
                      width: 150,
                      prop: "state",
                      render: ({ row }: { row: ActivityRelatedProductItem }) =>
                          Number(row.type) === 4 ? "上架" : Number(row.product?.state) === 1 ? "上架" : "下架"
                  },
                  {
                      key: "addTime",
                      label: "添加时间",
                      width: 150,
                      prop: "addTime",
                      render: ({ row }: { row: ActivityRelatedProductItem }) =>
                          row.addTime ? String(row.addTime).replace("T", " ").slice(0, 19) : "-"
                  }
              ]
            : []),
        {
            key: "operation",
            label: "操作",
            // Vue 新增页在窄表格中将操作列压缩，编辑页则保留删除/编辑的操作空间。
            width: isEdit ? 230 : 67,
            prop: "operation",
            render: ({ rowIndex }) => (
                <ZhButton type="primary" variant="text" onClick={() => removeItem(rowIndex)}>
                    删除
                </ZhButton>
            )
        }
    ];

    const candidateColumns: TableColumnProps<ActivityRelatedProductCandidate>[] = [
        {
            key: "selection",
            label: "",
            width: 64,
            prop: "selection",
            // Vue 的 selection 列在表头提供全选当前页；手工渲染单行勾选时也要保留此行为。
            renderHeader: () => {
                const pageKeys = candidates.map(candidate => itemKey(candidate));
                const selectedCount = pageKeys.filter(key => pendingKeys.includes(key)).length;
                return (
                    <ZhCheckbox
                        ariaLabel="全选当前页商品"
                        checked={pageKeys.length > 0 && selectedCount === pageKeys.length}
                        indeterminate={selectedCount > 0 && selectedCount < pageKeys.length}
                        onChange={event =>
                            setPendingKeys(current =>
                                event.target.checked
                                    ? [...new Set([...current, ...pageKeys])]
                                    : current.filter(key => !pageKeys.includes(key))
                            )
                        }
                    />
                );
            },
            render: ({ row }) => {
                const key = itemKey(row);
                const title = getItemTitle(row);
                return (
                    <ZhCheckbox
                        ariaLabel={`选择${title}`}
                        checked={pendingKeys.includes(key)}
                        onChange={event =>
                            setPendingKeys(current =>
                                event.target.checked ? [...current, key] : current.filter(currentKey => currentKey !== key)
                            )
                        }
                    />
                );
            }
        },
        {
            key: "productId",
            label: "商品编号",
            width: 176,
            prop: "productId",
            render: ({ row }) => String(row.productId || "-")
        },
        {
            key: "title",
            label: "商品名称",
            width: 350,
            prop: "title",
            render: ({ row }) => row.product?.title || "-"
        },
        {
            key: "productTypeName",
            label: "品类",
            // Vue 候选表在 1160px 弹窗内容区的实际列界线为 149/273/148px。
            width: 149,
            prop: "productTypeName",
            render: ({ row }) => row.product?.productTypeName || "-"
        },
        {
            key: "supplierName",
            label: "供应商",
            width: 273,
            prop: "supplierName",
            render: ({ row }) => getCandidateSupplierName(row)
        },
        {
            key: "type",
            label: "商品类型",
            width: 148,
            prop: "type",
            render: ({ row }) => productTypeLabels[Number(row.type)] || "-"
        }
    ];

    return (
        <div className="activity-product-list">
            <ZhButton type="primary" onClick={openDialog}>
                新增商品
            </ZhButton>
            <ZhTable<ActivityRelatedProductItem>
                className="activity-product-list__table"
                columns={selectedColumns}
                data={selectedItems}
                fit
                isShowResetBtn={false}
                isShowSearchBtn={false}
                pagination={false}
                rowKey={item => itemKey(item)}
            />
            <ZhDialog
                alignCenter
                className="activity-product-dialog-modal"
                footer={
                    <div className="activity-product-dialog__footer">
                        <ZhButton onClick={() => setOpen(false)}>取消</ZhButton>
                        <ZhButton type="primary" onClick={() => void submitDialog()}>
                            提交
                        </ZhButton>
                    </div>
                }
                open={open}
                title="新增商品"
                width={1200}
                zIndex={1000}
                onCancel={() => setOpen(false)}
            >
                <div className="activity-product-dialog">
                    <div className="activity-product-dialog__type-row">
                        <span className="required-label">商品类型</span>
                        <ZhRadioGroup
                            options={[
                                { label: "关联寄售/团购/捡漏商品", value: 1 },
                                { label: "新增活动商品", value: 2 }
                            ]}
                            value={dialogType}
                            onChange={event => {
                                const nextType = Number(event.target.value);
                                setDialogType(nextType);
                                if (nextType === 1 && !candidates.length) void queryCandidates("", 1);
                                if (nextType === 2) void querySuppliers();
                            }}
                        />
                    </div>

                    {dialogType === 1 ? (
                        <div className="activity-product-dialog__list-card">
                            <div className="activity-product-dialog__subtitle">商品列表</div>
                            <Spin spinning={loading}>
                                <ZhTable<ActivityRelatedProductCandidate>
                                    data={candidates}
                                    columns={candidateColumns}
                                    fit={false}
                                    pagination={false}
                                    rowKey={candidate => itemKey(candidate)}
                                    isShowSearchBtn={false}
                                    isShowResetBtn={false}
                                    tableHeader={
                                        <div className="activity-product-dialog__filters">
                                            <Input
                                                aria-label="商品搜索"
                                                className="activity-product-dialog__filter-keyword"
                                                placeholder="请输入商品名称/商品编号"
                                                value={keyword}
                                                onChange={event => setKeyword(event.target.value)}
                                            />
                                            <Input
                                                aria-label="候选商品品类"
                                                className="activity-product-dialog__filter-category"
                                                placeholder="请输入品类"
                                                value={productTypeName}
                                                onChange={event => setProductTypeName(event.target.value)}
                                            />
                                            <Input
                                                aria-label="候选商品供应商"
                                                className="activity-product-dialog__filter-supplier"
                                                placeholder="请输入供应商名称"
                                                value={supplierName}
                                                onChange={event => setSupplierName(event.target.value)}
                                            />
                                            <ZhSelect
                                                allowClear
                                                aria-label="候选商品类型"
                                                className="activity-product-dialog__filter-type"
                                                options={[1, 2, 3].map(type => ({
                                                    label: productTypeLabels[type],
                                                    value: String(type)
                                                }))}
                                                placeholder="请选择商品类型"
                                                value={candidateType || undefined}
                                                onChange={value => setCandidateType(String(value || ""))}
                                            />
                                            <ZhButton
                                                className="activity-product-dialog__filter-search"
                                                icon={<TableSearchIcon name="search" />}
                                                type="primary"
                                                onClick={() => void queryCandidates(keyword, 1)}
                                            >
                                                搜索
                                            </ZhButton>
                                            <ZhButton
                                                aria-label="重置商品筛选"
                                                className="activity-product-dialog__filter-reset"
                                                icon={<TableSearchIcon name="reset" />}
                                                onClick={() => {
                                                    setKeyword("");
                                                    setProductTypeName("");
                                                    setSupplierName("");
                                                    setCandidateType("");
                                                    void queryCandidates("", 1, {
                                                        productTypeName: "",
                                                        supplierName: "",
                                                        candidateType: ""
                                                    });
                                                }}
                                            />
                                            {selectorError ? (
                                                <Typography.Text type="danger" role="alert">
                                                    {selectorError}
                                                </Typography.Text>
                                            ) : null}
                                        </div>
                                    }
                                    append={
                                        <div className="activity-product-dialog__pagination">
                                            <ZhPagination
                                                current={page}
                                                pageSize={10}
                                                total={total}
                                                onChange={nextPage => void queryCandidates(keyword, nextPage)}
                                            />
                                        </div>
                                    }
                                />
                            </Spin>
                        </div>
                    ) : (
                        <Form
                            className="activity-product-dialog__activity-form"
                            form={activityProductForm}
                            labelCol={{ flex: "108px" }}
                            wrapperCol={{ flex: 1 }}
                        >
                            <div className="activity-product-dialog__form-grid">
                                <Form.Item
                                    label="供应商"
                                    name="supplierId"
                                    required
                                    rules={[{ required: true, message: "请选择供应商" }]}
                                >
                                    <ZhSelect
                                        allowClear
                                        showSearch
                                        options={supplierOptions}
                                        placeholder="请选择供应商"
                                        onSearch={keyword => void querySuppliers(keyword)}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="活动商品"
                                    name="name"
                                    required
                                    rules={[{ required: true, message: "请输入商品名称" }]}
                                >
                                    <Input placeholder="请输入活动商品" />
                                </Form.Item>
                                <Form.Item
                                    label="库存量"
                                    name="stock"
                                    required
                                    rules={[{ required: true, message: "请输入库存量" }]}
                                >
                                    <ZhInputNumber min={1} placeholder="请输入库存量" suffix="吨" />
                                </Form.Item>
                                <Form.Item
                                    label="定价方式"
                                    name="pricingMethod"
                                    required
                                    rules={[{ required: true, message: "请选择定价方式" }]}
                                >
                                    <ZhSelect
                                        allowClear
                                        options={[
                                            { label: "市场价+活动单价", value: 1 },
                                            { label: "比市场价直降", value: 2 },
                                            { label: "以活动现场公布为准", value: 3 }
                                        ]}
                                        placeholder="请选择定价方式"
                                    />
                                </Form.Item>
                                {Number(pricingMethod) === 1 ? (
                                    <>
                                        <Form.Item
                                            label="市场价"
                                            name="marketPrice"
                                            required
                                            rules={[{ required: true, message: "请输入市场价" }]}
                                        >
                                            <ZhInputNumber min={0.01} placeholder="请输入市场价" suffix="元/吨" />
                                        </Form.Item>
                                        <Form.Item
                                            label="活动单价"
                                            name="estimatedUnitPrice"
                                            required
                                            rules={[{ required: true, message: "请输入活动单价" }]}
                                        >
                                            <ZhInputNumber min={0.01} placeholder="请输入活动单价" suffix="元/吨" />
                                        </Form.Item>
                                    </>
                                ) : null}
                                {Number(pricingMethod) === 2 ? (
                                    <Form.Item
                                        label="比市场价直降"
                                        name="lowerThanMarketPrice"
                                        required
                                        rules={[{ required: true, message: "请输入直降单价" }]}
                                    >
                                        <ZhInputNumber min={0.01} placeholder="请输入直降单价" suffix="元/吨" />
                                    </Form.Item>
                                ) : null}
                                <Form.Item
                                    label="是否允许超售"
                                    name="hasOverbooking"
                                    required
                                    rules={[{ required: true, message: "请选择是否允许超售" }]}
                                >
                                    <ZhRadioGroup
                                        options={[
                                            { label: "是", value: 1 },
                                            { label: "否", value: 0 }
                                        ]}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="起订吨数"
                                    name="minOrderTon"
                                    required
                                    rules={[{ required: true, message: "请输入起订吨数" }]}
                                >
                                    <ZhInputNumber min={1} placeholder="请输入起订吨数" suffix="吨" />
                                </Form.Item>
                                <Form.Item
                                    label="最小调整吨数"
                                    name="minAdjustmentTon"
                                    required
                                    rules={[{ required: true, message: "请输入最小调整吨数" }]}
                                >
                                    <ZhInputNumber min={1} placeholder="请输入最小调整吨数" suffix="吨" />
                                </Form.Item>
                                <Form.Item
                                    className="activity-product-dialog__form-full activity-product-dialog__order-rule"
                                    label="下单要求"
                                    name="orderRule"
                                    required
                                    rules={[{ required: true, message: "请选择下单要求" }]}
                                >
                                    <ZhRadioGroup
                                        options={[
                                            { label: "固定金额", value: 1 },
                                            { label: "阶梯金额", value: 2 }
                                        ]}
                                    />
                                </Form.Item>
                                {Number(orderRule) === 1 ? (
                                    <Form.Item
                                        label="意向金"
                                        name="intentionDeposit"
                                        required
                                        rules={[{ required: true, message: "请输入意向金" }]}
                                    >
                                        <ZhInputNumber min={1} placeholder="请输入意向金" suffix="元" />
                                    </Form.Item>
                                ) : null}
                                {Number(orderRule) === 2 ? (
                                    <>
                                        <Form.Item
                                            label="最低意向金"
                                            name="intentionDeposit"
                                            required
                                            rules={[{ required: true, message: "请输入意向金" }]}
                                        >
                                            <ZhInputNumber min={0} placeholder="请输入意向金" suffix="元" />
                                        </Form.Item>
                                        <Form.Item
                                            label="每增加"
                                            name="tonStep"
                                            required
                                            rules={[{ required: true, message: "请输入吨数" }]}
                                        >
                                            <ZhInputNumber min={1} placeholder="请输入吨数" suffix="吨" />
                                        </Form.Item>
                                        <Form.Item
                                            label="增加"
                                            name="amountStep"
                                            required
                                            rules={[{ required: true, message: "请输入金额" }]}
                                        >
                                            <ZhInputNumber min={1} placeholder="请输入金额" suffix="元" />
                                        </Form.Item>
                                    </>
                                ) : null}
                                <Form.Item
                                    className="activity-product-dialog__form-full activity-product-dialog__detail"
                                    label="商品详情"
                                    name="detail"
                                    required
                                    rules={[{ required: true, message: "请输入商品详情" }]}
                                >
                                    <ActivityProductEditor />
                                </Form.Item>
                                <Form.Item
                                    className="activity-product-dialog__form-full activity-product-dialog__image"
                                    label="图片"
                                    required
                                >
                                    <div className="activity-product-dialog__upload-hint">
                                        支持JPEG、PNG、WEBP、AVIF格式，单张最大10MB，请勿包含敏感信息或侵权内容
                                    </div>
                                    <div className="activity-product-dialog__upload-list">
                                        {pictureFiles.length < 6 ? (
                                            <Imgs
                                                fileList={pictureFiles}
                                                fileSize={10}
                                                fileType={["image/jpeg", "image/png", "image/webp", "image/avif"]}
                                                limit={6}
                                                listType="text"
                                                showFileList={false}
                                                onUpdateData={file => {
                                                    const current = activityProductForm.getFieldValue("pictureFileIds") || [];
                                                    const replacementIndex = pictureReplacementIndex.current;
                                                    if (replacementIndex == null) {
                                                        activityProductForm.setFieldValue("pictureFileIds", [...current, file.id]);
                                                        if (!mainImgUrl) setMainImgUrl(file.path || "");
                                                    } else {
                                                        const nextIds = [...current];
                                                        nextIds[replacementIndex] = file.id;
                                                        activityProductForm.setFieldValue("pictureFileIds", nextIds);
                                                        if (replacementIndex === 0) setMainImgUrl(file.path || "");
                                                        pictureReplacementIndex.current = null;
                                                    }
                                                }}
                                                onUpdateFileList={files => {
                                                    const replacementIndex = pictureReplacementIndex.current;
                                                    if (replacementIndex == null) {
                                                        setPictureFiles(files);
                                                        return;
                                                    }
                                                    const replacementFile = files.at(-1);
                                                    if (!replacementFile) return;
                                                    const nextFiles = files.slice(0, -1);
                                                    nextFiles[replacementIndex] = replacementFile;
                                                    setPictureFiles(nextFiles);
                                                }}
                                            >
                                                <span className="activity-product-dialog__media-trigger">
                                                    <i aria-hidden="true" className="iconfont icon-shangchuan" />
                                                    上传图片
                                                </span>
                                            </Imgs>
                                        ) : null}
                                        {pictureFiles.map((file, index) => (
                                            <div className="activity-product-dialog__uploaded-image" key={file.uid || file.url}>
                                                <ZhImage alt={file.name || "活动商品图片"} preview src={file.url} />
                                                <button
                                                    aria-label={`删除${file.name || "活动商品图片"}`}
                                                    type="button"
                                                    className="activity-product-dialog__image-delete"
                                                    onClick={() => {
                                                        const nextFiles = pictureFiles.filter(item => item !== file);
                                                        activityProductForm.setFieldValue("pictureFileIds", getPictureFileIds(nextFiles));
                                                        setPictureFiles(nextFiles);
                                                        if (file.url === mainImgUrl) setMainImgUrl(nextFiles[0]?.url || "");
                                                    }}
                                                >
                                                    ×
                                                </button>
                                                <div className="activity-product-dialog__image-actions">
                                                    {index > 0 ? (
                                                        <button
                                                            aria-label={`设置主图 ${file.name || `图片${index + 1}`}`}
                                                            type="button"
                                                            onClick={() => setPictureAsMain(file, index)}
                                                        >
                                                            设置主图
                                                        </button>
                                                    ) : null}
                                                    <button
                                                        aria-label={`替换图片 ${file.name || `图片${index + 1}`}`}
                                                        type="button"
                                                        onClick={event => replacePicture(event, index)}
                                                    >
                                                        替换图片
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Form.Item>
                                <Form.Item className="activity-product-dialog__form-full" label="视频">
                                    <div className="activity-product-dialog__upload-hint">
                                        仅支持mov,mp4格式，大小不超过200M
                                    </div>
                                    <Video
                                        controls={false}
                                        fileSize={200}
                                        fileType={["video/mp4", "video/quicktime"]}
                                        height="122px"
                                        onUpdateData={file => activityProductForm.setFieldValue("videoFileId", file.id)}
                                        previewActionClassName="activity-product-dialog__video-reupload"
                                        previewClassName="activity-product-dialog__video-preview"
                                        width="232px"
                                    >
                                        <span className="activity-product-dialog__media-trigger">
                                            <i aria-hidden="true" className="iconfont icon-shangchuan" />
                                            上传视频
                                        </span>
                                    </Video>
                                </Form.Item>
                            </div>
                        </Form>
                    )}
                </div>
            </ZhDialog>
        </div>
    );
}

export default ActivityRelatedProductSelector;
